import { Form, FormInstance } from 'antd';
import { useEffect, useRef, useState } from 'react';

interface ResetDependency {
  watchField: string; // campo que se observa (como empresa)
  resetFields: string[]; // campos que se deben resetear cuando cambia
}
/**
 * @description permite resetear campos de un formulario cuando cambia un campo observado.
 * @param form instancia del formulario de Ant Design
 * @param withoutInitial indica si se deben establecer los valores iniciales (para forms de creación)
 * @param dependencies lista de dependencias que contienen el campo a observar y los campos a resetear
 * @param options opciones adicionales
 * @returns un objeto con `initialValuesSetted` y `setInitialValuesSetted`
 * @example
 * ```tsx
 * const { initialValuesSetted, setInitialValuesSetted } = useFormInitialSet(
 *   form,
 *   { empresa: 'Empresa 1', movil: 'Movil 1' },
 *   [
 *     {
 *       watchField: 'empresa', // campo a observar
 *       resetFields: ['movil'], // campos a resetear cuando cambia 'empresa'
 *     },
 *   ],
 * );
 * ```
 */

const useFormInitialSet = (
  form: FormInstance | undefined,
  initialValues: Record<string, any> | undefined,
  dependencies: ResetDependency[],
  options?: {
    withoutInitial?: boolean; // si es true, no se establecen los valores iniciales
    afterInitialSet?: () => void; // función que se ejecuta después de establecer los valores iniciales
  },
) => {
  const [initialValuesSetted, setInitialValuesSetted] = useState(false);
  const previousValues = useRef<Record<string, any>>({});
  const watchedValues = Form.useWatch([], form);

  // Set initial values solo una vez
  useEffect(() => {
    if (!form) return;

    if (options?.withoutInitial) {
      setInitialValuesSetted(true);

      return;
    }

    if (!initialValuesSetted && initialValues) {
      const flatValues = Object.fromEntries(
        Object.entries(initialValues).map(([key, value]) => [
          key,
          value && typeof value === 'object' && 'id' in value ? value.id : value,
        ]),
      );

      form.setFieldsValue(flatValues);
      options?.afterInitialSet?.();
      setInitialValuesSetted(true);
      previousValues.current = { ...flatValues };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, initialValues, initialValuesSetted, options?.withoutInitial]);

  // Reset dependencias si cambia algún campo observado
  useEffect(() => {
    if (!form || !initialValuesSetted) return;

    dependencies.forEach(({ watchField, resetFields }) => {
      const currentValue = watchedValues?.[watchField];
      const previousValue = previousValues.current[watchField];

      if (currentValue !== previousValue) {
        const resetValues = Object.fromEntries(resetFields.map(field => [field, undefined]));

        form.setFieldsValue(resetValues);
        previousValues.current[watchField] = currentValue;
      }
    });
  }, [dependencies, form, initialValuesSetted, watchedValues]);

  return {
    initialValuesSetted,
    setInitialValuesSetted,
  };
};

export default useFormInitialSet;
